import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import FlowLogo from "@/components/FlowLogo";
import { Badge } from "@/components/ui/badge";

interface PageHeroProps {
  title: string;
  subtitle?: string;
  badge?: ReactNode;
  className?: string;
  showLogo?: boolean;
  logoSize?: "sm" | "md" | "lg";
  logoSlot?: ReactNode;
  titleClassName?: string;
  children?: ReactNode;
}

export function PageHero({
  title,
  subtitle,
  badge,
  className,
  showLogo = true,
  logoSize = "lg",
  logoSlot,
  titleClassName,
  children,
}: PageHeroProps) {
  const logoSizeClasses = {
    sm: "w-24 h-24 sm:w-32 sm:h-32",
    md: "w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48",
    lg: "w-40 h-40 sm:w-56 sm:h-56 lg:w-64 lg:h-64",
  };

  return (
    <div className={cn("w-full", className)}>
      <div className="grid md:grid-cols-2 gap-6 items-start">
        {/* Mobile: Logo first */}
        {showLogo && (
          <div className="flex justify-center md:hidden order-first">
            {logoSlot ?? (
              <div className={cn("relative", logoSizeClasses[logoSize])}>
                <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_30%,hsl(var(--primary)/0.15),transparent_50%)]" />
                <div className="absolute inset-[8%] rounded-full bg-card shadow-lg border border-border/50 grid place-items-center">
                  <FlowLogo size={logoSize} shape="circle" animate={false} />
                </div>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
                  <Badge variant="secondary" className="bg-card/90 backdrop-blur-sm border border-border/50 px-2 py-0.5 text-xs">
                    <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                    LIVE
                  </Badge>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Text content - left on desktop */}
        <div className="space-y-3 text-center md:text-left order-2 md:order-1">
          {badge && (
            <div className="mb-2 inline-flex justify-center md:justify-start">
              {badge}
            </div>
          )}
          <h1 className={cn("text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground", titleClassName)}>
            {title}
          </h1>
          {subtitle && (
            <p className="text-muted-foreground text-base md:text-lg max-w-lg mx-auto md:mx-0">
              {subtitle}
            </p>
          )}
          {children}
        </div>

        {/* Logo - right on desktop */}
        {showLogo && (
          <div className="hidden md:flex justify-center md:justify-end order-1 md:order-2">
            {logoSlot ?? (
              <div className={cn("relative", logoSizeClasses[logoSize])}>
                <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_30%,hsl(var(--primary)/0.15),transparent_50%)]" />
                <div className="absolute inset-[8%] rounded-full bg-card shadow-lg border border-border/50 grid place-items-center">
                  <FlowLogo size={logoSize} shape="circle" animate={false} />
                </div>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                  <Badge variant="secondary" className="bg-card/90 backdrop-blur-sm border border-border/50 px-3 py-1 text-xs">
                    <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-primary animate-pulse" aria-hidden="true" />
                    LIVE
                  </Badge>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PageHero;
